/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login page if user is not authenticated.
 *
 * Usage:
 *   <Route path="/dashboard" element={
 *     <ProtectedRoute>
 *       <DashboardPage />
 *     </ProtectedRoute>
 *   } />
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Optional subscription tier requirement
   * If specified, user must have one of these tiers to access
   */
  requiredTier?: ('free' | 'gold' | 'platinum')[];
}

export function ProtectedRoute({ children, requiredTier }: ProtectedRouteProps) {
  const { user, profile, loading, profileLoading } = useAuth();
  const location = useLocation();

  // Only show loading state while doing INITIAL authentication check
  // Don't block on profileLoading once we have a user (allows instant navigation)
  if (loading || (profileLoading && !user)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <div style={{
          textAlign: 'center',
          color: 'white',
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{ fontSize: '18px', margin: 0 }}>Loading...</p>
          <p style={{ fontSize: '12px', marginTop: '10px', opacity: 0.7 }}>
            {loading ? 'Checking authentication...' : 'Loading profile...'}
          </p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  // Preserve the attempted URL for redirect after login
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Check subscription tier if required
  if (requiredTier && profile) {
    if (!requiredTier.includes(profile.subscription_tier)) {
      return (
        <Navigate
          to="/account/billing"
          state={{
            message: `This feature requires a ${requiredTier.join(' or ')} subscription`,
            requiredTier,
          }}
          replace
        />
      );
    }
  }

  // User is authenticated and has required tier (if specified)
  return <>{children}</>;
}

/**
 * Public Only Route Component
 *
 * Wraps routes that should only be accessible when NOT authenticated.
 * Redirects to dashboard if user is already logged in.
 *
 * Usage:
 *   <Route path="/login" element={
 *     <PublicOnlyRoute>
 *       <LoginPage />
 *     </PublicOnlyRoute>
 *   } />
 */
export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect to dashboard if already authenticated
  // Always go to /dashboard to prevent redirect loops
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
